/**
 * Centralized icon exports using Heroicons
 * Standard sizing: w-5 h-5 (20x20px) for consistency across admin panel
 */
import {
  // Navigation Icons
  HomeIcon,
  ChartBarIcon,
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,

  // Action Icons
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,

  // Status & Info Icons
  BanknotesIcon,
  PhoneIcon,
  DocumentTextIcon,
  BoltIcon,
  GlobeAltIcon,
  MapPinIcon,

  // Additional Common Icons
  UserIcon,
  UserCircleIcon,
  UserPlusIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTopRightOnSquareIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  EllipsisVerticalIcon,
  TagIcon,
  BuildingStorefrontIcon,
  HomeModernIcon,
  MapIcon as HeroiconsMapIcon,
  FlagIcon as HeroiconsFlagIcon,
  ShieldCheckIcon,
  CubeIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  ClockIcon,
  StarIcon,
  HeartIcon,
  ShareIcon,
  PrinterIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  PaperClipIcon,
  EnvelopeIcon,
  BeakerIcon,
  WrenchScrewdriverIcon,
  TableCellsIcon,
  ListBulletIcon,
  Squares2X2Icon,
  AdjustmentsHorizontalIcon,
  ArrowLeftIcon,
  EyeSlashIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  BookmarkIcon,
} from '@heroicons/react/24/outline'

// Default icon props for consistency
const iconProps = {
  className: 'w-5 h-5',
  'aria-hidden': true,
}

// Navigation Icons
export const DashboardIcon = (props: React.ComponentProps<'svg'>) => (
  <ChartBarIcon {...iconProps} {...props} />
)

export const AdminHomeIcon = (props: React.ComponentProps<'svg'>) => (
  <HomeIcon {...iconProps} {...props} />
)

export const StructuresIcon = (props: React.ComponentProps<'svg'>) => (
  <BuildingOffice2Icon {...iconProps} {...props} />
)

export const PropertiesIcon = (props: React.ComponentProps<'svg'>) => (
  <BuildingOfficeIcon {...iconProps} {...props} />
)

export const PeopleIcon = (props: React.ComponentProps<'svg'>) => (
  <UserGroupIcon {...iconProps} {...props} />
)

export const CalendarNavIcon = (props: React.ComponentProps<'svg'>) => (
  <CalendarDaysIcon {...iconProps} {...props} />
)

export const SettingsIcon = (props: React.ComponentProps<'svg'>) => (
  <Cog6ToothIcon {...iconProps} {...props} />
)

// Action Icons
export const AddIcon = (props: React.ComponentProps<'svg'>) => (
  <PlusIcon {...iconProps} {...props} />
)

export const SuccessIcon = (props: React.ComponentProps<'svg'>) => (
  <CheckIcon {...iconProps} {...props} />
)

export const ErrorIcon = (props: React.ComponentProps<'svg'>) => (
  <XMarkIcon {...iconProps} {...props} />
)

export const ViewIcon = (props: React.ComponentProps<'svg'>) => (
  <EyeIcon {...iconProps} {...props} />
)

export const EditIcon = (props: React.ComponentProps<'svg'>) => (
  <PencilIcon {...iconProps} {...props} />
)

export const DeleteIcon = (props: React.ComponentProps<'svg'>) => (
  <TrashIcon {...iconProps} {...props} />
)

export const CopyIcon = (props: React.ComponentProps<'svg'>) => (
  <ClipboardDocumentListIcon {...iconProps} {...props} />
)

export const RefreshIcon = (props: React.ComponentProps<'svg'>) => (
  <ArrowPathIcon {...iconProps} {...props} />
)

// Status & Info Icons
export const MoneyIcon = (props: React.ComponentProps<'svg'>) => (
  <BanknotesIcon {...iconProps} {...props} />
)

export const AnalyticsIcon = (props: React.ComponentProps<'svg'>) => (
  <ChartBarIcon {...iconProps} {...props} />
)

export const ContactIcon = (props: React.ComponentProps<'svg'>) => (
  <PhoneIcon {...iconProps} {...props} />
)

export const DocumentIcon = (props: React.ComponentProps<'svg'>) => (
  <DocumentTextIcon {...iconProps} {...props} />
)

export const UtilitiesIcon = (props: React.ComponentProps<'svg'>) => (
  <BoltIcon {...iconProps} {...props} />
)

export const GlobalIcon = (props: React.ComponentProps<'svg'>) => (
  <GlobeAltIcon {...iconProps} {...props} />
)

export const LocationIcon = (props: React.ComponentProps<'svg'>) => (
  <MapPinIcon {...iconProps} {...props} />
)

// User Icons
export const ClientIcon = (props: React.ComponentProps<'svg'>) => (
  <UserIcon {...iconProps} {...props} />
)

export const UserAvatarIcon = (props: React.ComponentProps<'svg'>) => (
  <UserCircleIcon {...iconProps} {...props} />
)

export const AddUserIcon = (props: React.ComponentProps<'svg'>) => (
  <UserPlusIcon {...iconProps} {...props} />
)

// Status Icons
export const InfoIcon = (props: React.ComponentProps<'svg'>) => (
  <InformationCircleIcon {...iconProps} {...props} />
)

export const WarningIcon = (props: React.ComponentProps<'svg'>) => (
  <ExclamationTriangleIcon {...iconProps} {...props} />
)

export const CompletedIcon = (props: React.ComponentProps<'svg'>) => (
  <CheckCircleIcon {...iconProps} {...props} />
)

export const CancelIcon = (props: React.ComponentProps<'svg'>) => (
  <XCircleIcon {...iconProps} {...props} />
)

// Additional Icons
export const ExternalLinkIcon = (props: React.ComponentProps<'svg'>) => (
  <ArrowTopRightOnSquareIcon {...iconProps} {...props} />
)

export const FilterIcon = (props: React.ComponentProps<'svg'>) => (
  <FunnelIcon {...iconProps} {...props} />
)

export const SearchIcon = (props: React.ComponentProps<'svg'>) => (
  <MagnifyingGlassIcon {...iconProps} {...props} />
)

export const MenuIcon = (props: React.ComponentProps<'svg'>) => (
  <Bars3Icon {...iconProps} {...props} />
)

export const MoreIcon = (props: React.ComponentProps<'svg'>) => (
  <EllipsisVerticalIcon {...iconProps} {...props} />
)

export const TagsIcon = (props: React.ComponentProps<'svg'>) => (
  <TagIcon {...iconProps} {...props} />
)

export const CommercialIcon = (props: React.ComponentProps<'svg'>) => (
  <BuildingStorefrontIcon {...iconProps} {...props} />
)

export const ResidentialIcon = (props: React.ComponentProps<'svg'>) => (
  <HomeModernIcon {...iconProps} {...props} />
)

export const MapIcon = (props: React.ComponentProps<'svg'>) => (
  <HeroiconsMapIcon {...iconProps} {...props} />
)

export const NationalityIcon = (props: React.ComponentProps<'svg'>) => (
  <FlagIcon {...iconProps} {...props} />
)

export const SecurityIcon = (props: React.ComponentProps<'svg'>) => (
  <ShieldCheckIcon {...iconProps} {...props} />
)

export const AttributesIcon = (props: React.ComponentProps<'svg'>) => (
  <CubeIcon {...iconProps} {...props} />
)

export const FlagIcon = (props: React.ComponentProps<'svg'>) => (
  <HeroiconsFlagIcon {...iconProps} {...props} />
)

export const DealsIcon = (props: React.ComponentProps<'svg'>) => (
  <ChatBubbleLeftRightIcon {...iconProps} {...props} />
)

export const ScheduleIcon = (props: React.ComponentProps<'svg'>) => (
  <CalendarIcon {...iconProps} {...props} />
)

export const TimeIcon = (props: React.ComponentProps<'svg'>) => (
  <ClockIcon {...iconProps} {...props} />
)

export const FavoriteIcon = (props: React.ComponentProps<'svg'>) => (
  <StarIcon {...iconProps} {...props} />
)

export const LikeIcon = (props: React.ComponentProps<'svg'>) => (
  <HeartIcon {...iconProps} {...props} />
)

export const ShareIconComp = (props: React.ComponentProps<'svg'>) => (
  <ShareIcon {...iconProps} {...props} />
)

export const PrintIcon = (props: React.ComponentProps<'svg'>) => (
  <PrinterIcon {...iconProps} {...props} />
)

export const DownloadIcon = (props: React.ComponentProps<'svg'>) => (
  <DocumentArrowDownIcon {...iconProps} {...props} />
)

export const UploadIcon = (props: React.ComponentProps<'svg'>) => (
  <DocumentArrowUpIcon {...iconProps} {...props} />
)

export const AttachmentIcon = (props: React.ComponentProps<'svg'>) => (
  <PaperClipIcon {...iconProps} {...props} />
)

export const EmailIcon = (props: React.ComponentProps<'svg'>) => (
  <EnvelopeIcon {...iconProps} {...props} />
)

export const TestIcon = (props: React.ComponentProps<'svg'>) => (
  <BeakerIcon {...iconProps} {...props} />
)

export const ToolsIcon = (props: React.ComponentProps<'svg'>) => (
  <WrenchScrewdriverIcon {...iconProps} {...props} />
)

export const TableIcon = (props: React.ComponentProps<'svg'>) => (
  <TableCellsIcon {...iconProps} {...props} />
)

export const ListIcon = (props: React.ComponentProps<'svg'>) => (
  <ListBulletIcon {...iconProps} {...props} />
)

export const GridIcon = (props: React.ComponentProps<'svg'>) => (
  <Squares2X2Icon {...iconProps} {...props} />
)

export const AdjustIcon = (props: React.ComponentProps<'svg'>) => (
  <AdjustmentsHorizontalIcon {...iconProps} {...props} />
)

export const MeasurementIcon = (props: React.ComponentProps<'svg'>) => (
  <AdjustmentsHorizontalIcon {...iconProps} {...props} />
)

export const BedIcon = (props: React.ComponentProps<'svg'>) => (
  <HomeModernIcon {...iconProps} {...props} />
)

// Custom Bathroom Icon (matching Heroicons style)
export const BathIcon = (props: React.ComponentProps<'svg'>) => (
  <svg
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    viewBox="0 0 24 24"
    {...iconProps}
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 12h2.25M5.25 12v6.75a1.5 1.5 0 001.5 1.5h9.5a1.5 1.5 0 001.5-1.5V12M5.25 12V9.75a3 3 0 013-3h4.5a3 3 0 013 3V12M21 12h-2.25M19.5 15.75v.75M4.5 15.75v.75"
    />
  </svg>
)

export const ServiceIcon = (props: React.ComponentProps<'svg'>) => (
  <ShieldCheckIcon {...iconProps} {...props} />
)

export const ChatIcon = (props: React.ComponentProps<'svg'>) => (
  <ChatBubbleLeftRightIcon {...iconProps} {...props} />
)

export const TrophyIcon = (props: React.ComponentProps<'svg'>) => (
  <StarIcon {...iconProps} {...props} />
)

export const HappyIcon = (props: React.ComponentProps<'svg'>) => (
  <HeartIcon {...iconProps} {...props} />
)

// Additional utility icons
export const BackIcon = (props: React.ComponentProps<'svg'>) => (
  <ArrowLeftIcon {...iconProps} {...props} />
)

export const HiddenIcon = (props: React.ComponentProps<'svg'>) => (
  <EyeSlashIcon {...iconProps} {...props} />
)

export const ChevronRightIconComp = (props: React.ComponentProps<'svg'>) => (
  <ChevronRightIcon {...iconProps} {...props} />
)

export const ChevronDownIconComp = (props: React.ComponentProps<'svg'>) => (
  <ChevronDownIcon {...iconProps} {...props} />
)

// Calendar specific icons
export const SaveIcon = (props: React.ComponentProps<'svg'>) => (
  <BookmarkIcon {...iconProps} {...props} />
)

// Social Media Icons (custom SVGs matching Heroicons style)
export const FacebookIcon = (props: React.ComponentProps<'svg'>) => (
  <svg
    fill="currentColor"
    viewBox="0 0 24 24"
    {...iconProps}
    {...props}
  >
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
)

export const WhatsAppIcon = (props: React.ComponentProps<'svg'>) => (
  <svg
    fill="currentColor"
    viewBox="0 0 24 24"
    {...iconProps}
    {...props}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
  </svg>
)

export const TwitterIcon = (props: React.ComponentProps<'svg'>) => (
  <svg
    fill="currentColor"
    viewBox="0 0 24 24"
    {...iconProps}
    {...props}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
)

export const LineIcon = (props: React.ComponentProps<'svg'>) => (
  <svg
    fill="currentColor"
    viewBox="0 0 24 24"
    {...iconProps}
    {...props}
  >
    <path d="M24 10.304c0-5.369-5.383-9.738-12-9.738S0 4.935 0 10.304c0 4.823 4.279 8.878 10.06 9.608.391.085.92.258 1.055.594.123.305.08.784.04 1.095l-.172 1.027c-.052.31-.242 1.21 1.061.659 1.303-.55 7.031-4.135 9.602-7.083 1.766-1.926 2.354-3.875 2.354-5.9" />
  </svg>
)

// Area/Size Icon (ruler/measurement)
export const AreaIcon = (props: React.ComponentProps<'svg'>) => (
  <svg
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    viewBox="0 0 24 24"
    {...iconProps}
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5"
    />
  </svg>
)

// Generic share icon for social sharing
export const SocialShareIcon = (props: React.ComponentProps<'svg'>) => (
  <ShareIcon {...iconProps} {...props} />
)

// Re-export ClockIcon for calendar components (already exists as TimeIcon but components expect ClockIcon)
export { TimeIcon as ClockIcon }

// Re-export missing icons for calendar components
export { DeleteIcon as TrashIcon }
export { ClientIcon as UserIcon }

// Icon size variants
export const iconSizes = {
  xs: 'w-3 h-3', // 12px
  sm: 'w-4 h-4', // 16px
  md: 'w-5 h-5', // 20px (default)
  lg: 'w-6 h-6', // 24px
  xl: 'w-8 h-8', // 32px
  '2xl': 'w-10 h-10', // 40px
}

/**
 * Utility function to create an icon with custom size
 */
export const createIconWithSize = (
  IconComponent: React.ComponentType<any>,
  size: keyof typeof iconSizes = 'md'
) => {
  const SizedIcon = (props: React.ComponentProps<'svg'>) => (
    <IconComponent
      className={`${iconSizes[size]} ${props.className || ''}`}
      aria-hidden="true"
      {...props}
    />
  )
  SizedIcon.displayName = `IconWithSize(${IconComponent.displayName || IconComponent.name || 'Component'})`
  return SizedIcon
}
